import { useState } from "react";
import { Topic } from "../../../types/content";
import { TopicItem } from "./TopicItem";
import { AnimatePresence, motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, ChevronDown, ChevronUp, SlidersHorizontal, ArrowUpDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface TopicListProps {
  topics: Topic[];
  selectedTopics: string[];
  onTopicSelect: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onDeleteTopic: (id: string) => void;
  onGenerateContent: (topicId: string) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  onCreateTopic: () => void;
  isLoading?: boolean;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  onSortChange?: (field: string, direction: "asc" | "desc") => void;
}

export const TopicList = ({
  topics = [],
  selectedTopics = [],
  onTopicSelect,
  onToggleFavorite,
  onDeleteTopic,
  onGenerateContent,
  searchQuery,
  setSearchQuery,
  onCreateTopic,
  isLoading = false,
  sortField = "created",
  sortDirection = "desc",
  onSortChange
}: TopicListProps) => {
  const handleSort = (field: string) => {
    if (onSortChange) {
      if (sortField === field) {
        onSortChange(field, sortDirection === "asc" ? "desc" : "asc");
      } else {
        onSortChange(field, "desc");
      }
    }
  };

  const sortOptions = [
    { value: "title", label: "Title" },
    { value: "type", label: "Content Type" },
    { value: "audience", label: "Target Audience" },
    { value: "created", label: "Date Created" },
  ];

  const currentSortLabel = sortOptions.find(option => option.value === sortField)?.label || "Date Created";

  return (
    <div className="space-y-4">
      {/* Topics Count */}
      <div className="flex justify-between items-center px-1">
        <div className="text-sm text-zinc-500 dark:text-zinc-400">
          {topics.length} {topics.length === 1 ? "topic" : "topics"}
        </div>
      </div>

      {/* Topics Table */}
      <div className="bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-zinc-50 dark:bg-zinc-800/80">
            <TableRow className="dark:border-zinc-700">
              <TableHead className="w-[45%]">
                <span className="font-medium text-zinc-700 dark:text-zinc-300">Topic</span>
              </TableHead>
              <TableHead className="w-[20%]">
                <span className="font-medium text-zinc-700 dark:text-zinc-300">Target Audience</span>
              </TableHead>
              <TableHead className="w-[15%]">
                <span className="font-medium text-zinc-700 dark:text-zinc-300">Type</span>
              </TableHead>
              <TableHead className="w-[20%] text-right">
                <span className="font-medium text-zinc-700 dark:text-zinc-300">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <AnimatePresence>
              {isLoading ? (
                // Loading skeletons
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`} className="border-b border-zinc-200 dark:border-zinc-700">
                    <TableCell className="py-4">
                      <Skeleton className="h-6 w-full dark:bg-zinc-700" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-6 w-24 dark:bg-zinc-700" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-6 w-20 dark:bg-zinc-700" />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Skeleton className="h-8 w-8 rounded-full dark:bg-zinc-700" />
                        <Skeleton className="h-8 w-8 rounded-full dark:bg-zinc-700" />
                        <Skeleton className="h-8 w-24 rounded-md dark:bg-zinc-700" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : topics && topics.length > 0 ? (
                topics.map((topic) => (
                  topic && (
                    <TableRow
                      key={topic.id}
                      className={cn(
                        "border-b border-zinc-200 dark:border-zinc-700 group transition-colors cursor-pointer",
                        selectedTopics.includes(topic.id) 
                          ? "bg-blue-50/70 dark:bg-blue-900/20 hover:bg-blue-50 dark:hover:bg-blue-900/30" 
                          : "hover:bg-zinc-50/80 dark:hover:bg-zinc-700/50"
                      )}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button') || 
                            (e.target as HTMLElement).closest('[role="menuitem"]')) return;
                        onTopicSelect(topic.id);
                      }}
                    >
                      <TopicItem
                        topic={topic}
                        isSelected={selectedTopics.includes(topic.id)}
                        onSelect={onTopicSelect}
                        onToggleFavorite={onToggleFavorite}
                        onDelete={onDeleteTopic}
                        onGenerateContent={onGenerateContent}
                      />
                    </TableRow>
                  )
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center space-y-2 py-8 text-zinc-500 dark:text-zinc-400">
                      <p className="text-sm">No topics found matching your criteria</p>
                      <p className="text-xs text-zinc-400 dark:text-zinc-500">Try adjusting your search or filters</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </AnimatePresence>
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
